import { faSpinner } from '@fortawesome/pro-regular-svg-icons/faSpinner';
import { ScheduleModel } from '@maxpreps/apiserviceclients/apiModels';
import useRequestContext from '@maxpreps/shared/contexts/RequestContext';
import useTeamContext from '@maxpreps/shared/contexts/TeamContext';
import FormButtonManager from '@maxpreps/shared/corecomponents/Form/FormButtonManager';
import {
  displayToastMessage,
  ToastMode
} from '@maxpreps/shared/corecomponents/Toastify/Toast';
import { copySchedule } from '@maxpreps/shared/data/ghost-apis/contests-api';
import { replace as routerReplace } from '@maxpreps/shared/functions/primitiveHelpers/routerHelpers';
import { useToken } from '@maxpreps/shared/redux/user/userHooks';
import Font from '@maxpreps/shared/styles/font';
import IconButton from '@maxpreps/shared/ui/Button/IconButton';
import CardHeader from '@maxpreps/shared/ui/Card/CardHeader';
import StyledCardFooter from '@maxpreps/shared/ui/Card/StyledCardFooter';
import Icon from '@maxpreps/shared/ui/Icon/Icon';
import Modal from '@maxpreps/shared/ui/Modal/Modal';
import RadioList from '@maxpreps/shared/ui/RadioList/RadioList';
import StyledText from '@maxpreps/shared/ui/Text/StyledText';
import TextInput from '@maxpreps/shared/ui/TextInput/TextInput';
import { Form, Formik } from 'formik';
import dynamic from 'next/dynamic';
import React, { useState } from 'react';
import getCSRRewriteAttributes from '../../utils/getCSRRewriteAttributes';
import { getTeamUrl } from './../../utils/navigation';
import { Page } from './../../utils/pages';

interface ICopyScheduleModalProps {
  otherLevelTeams: any[];
  closeModal: Function;
}

const CopyScheduleModal: React.FC<ICopyScheduleModalProps> = ({
  otherLevelTeams = [],
  closeModal
}) => {
  const teamContext = useTeamContext();
  const token = useToken();
  const { appType } = useRequestContext();

  const { teamId, sportSeasonId, canonicalUrl, allSeasonId } = teamContext.data;
  const { scheduleModel: scheduleModelKey } =
    teamContext.sportData as never as {
      scheduleModel: keyof ScheduleModel;
    };

  const [isLoading, setIsLoading] = useState(false);
  const [selectedValue, setSelectedValue] = useState(
    otherLevelTeams[0]?.sportSeasonId
  );

  const otherTeamRadioList = otherLevelTeams.map(otherTeam => ({
    value: otherTeam.sportSeasonId,
    label: `${otherTeam.gender} ${otherTeam.level} ${otherTeam.sport} (${otherTeam.season} ${otherTeam.year})`
  }));

  return (
    <Modal onOutClick={closeModal} maxWidth="512px" portalId="copy-schedule">
      <Formik
        enableReinitialize
        initialValues={{
          defaultTime: '17:00',
          fromSportSeasonId: otherTeamRadioList[0].value
        }}
        validateOnBlur={false}
        onSubmit={async values => {
          setIsLoading(true);

          const [, error, json] = await copySchedule(
            teamId,
            sportSeasonId,
            values.fromSportSeasonId,
            values.defaultTime,
            token
          );
          if (error) {
            displayToastMessage(
              json?.status === 400
                ? json.message
                : error.message ?? 'Oops. Something went wrong.',
              ToastMode.Error
            );
          } else {
            if (json.status === 206) {
              displayToastMessage(
                `Schedule partly copied. Some contests could not be created for this team. The site will update in 15-30 min.`,
                ToastMode.Success
              );
            } else {
              displayToastMessage(
                `Schedule copied. The site will update in 15-30 min.`,
                ToastMode.Success
              );
            }

            // CSR "Refresh" to get the updated calculated schedule.
            const csrAttributes = getCSRRewriteAttributes(
              getTeamUrl(Page.Schedule, canonicalUrl, { admin: 1 }),
              teamId,
              sportSeasonId,
              allSeasonId,
              appType,
              undefined,
              ScheduleModel[scheduleModelKey]
            );

            routerReplace(csrAttributes.href, csrAttributes.as);

            closeModal();
          }

          setIsLoading(false);
        }}
      >
        {({ values, setValues, handleChange }) => (
          <Form>
            <CardHeader
              title="Copy Games to your Schedule"
              rightAction={
                isLoading ? (
                  <Icon icon={faSpinner} spin />
                ) : (
                  // @ts-ignore
                  <IconButton onClick={closeModal} />
                )
              }
            />
            <div className="card-padding">
              <StyledText className={Font.f14_tall}>
                Select a team schedule to copy.
              </StyledText>
              <RadioList
                name="schedules"
                items={otherTeamRadioList}
                value={selectedValue}
                setValue={v => {
                  setSelectedValue(v);
                  setValues({
                    ...values,
                    fromSportSeasonId: v
                  });
                }}
              />

              <TextInput
                placeholder="Set Default Game Time (Optional)"
                name="defaultTime"
                onChange={handleChange}
                value={values.defaultTime}
                type="time"
              />
            </div>

            <StyledCardFooter>
              <FormButtonManager
                isLoading={isLoading}
                onCancel={closeModal}
                onSave={true}
              />
            </StyledCardFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default dynamic(() => Promise.resolve(CopyScheduleModal), {
  ssr: false
});
